// src/maker/PsycholaborDashboard.js
import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Autocomplete,
    TextField,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    FilterList as FilterIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import api from '../config/api';
import { useNavigate } from 'react-router-dom';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const PsycholaborDashboard = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    onEditReport 
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { authToken, project, user, showAlert } = useStore.getState();

    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reprocessing, setReprocessing] = useState({});

    // Filtros client-side sobre los reportes ya cargados
    const [userFilter, setUserFilter] = useState(null);
    const [applicantFilter, setApplicantFilter] = useState(null);
    const [positionFilter, setPositionFilter] = useState(null);

    const language = i18n.language === 'es' ? 'es' : 'en';

    // Cargar evaluaciones
    const loadReports = async () => {
        if (!project) {
            setError(t('dashboard.selectProject'));
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { data } = await api.get('/reports', {
                params: {
                    projectId: project,
                    ...(startDate && { startDate }),
                    ...(endDate && { endDate }),
                },
                headers: { Authorization: `Bearer ${authToken}` },
            });

            setReports(data.data || []);
        } catch (err) {
            console.error('Error loading reports:', err);
            setError(t('reports.reportList.loadError'));
        } finally {
            setLoading(false);
        }
    };

    // Cargar al montar
    useEffect(() => {
        loadReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Aplicar filtros
    const applyFilters = () => {
        loadReports();
    };

    // Formatear fecha
    const formatDate = (timestamp) => {
        if (!timestamp) return t('dateNotAvailable');

        try {
            let date;
            if (timestamp._seconds) {
                date = new Date(timestamp._seconds * 1000);
            } else if (timestamp.seconds) {
                date = new Date(timestamp.seconds * 1000);
            } else if (typeof timestamp.toDate === 'function') {
                date = timestamp.toDate();
            } else if (timestamp instanceof Date) {
                date = timestamp;
            } else if (typeof timestamp === 'string') {
                date = new Date(timestamp);
            } else if (typeof timestamp === 'number') {
                date = new Date(timestamp);
            } else {
                return t('dateNotAvailable');
            }

            if (isNaN(date.getTime())) {
                return t('dateNotAvailable');
            }

            return date.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return t('dateNotAvailable');
        }
    };

    // Obtener color del estado
    const getStatusColor = (status) => {
        if (!status) return 'default';
        if (status.includes('error') || status.includes('fallido')) return 'error';
        if (status.includes('completado')) return 'success';
        if (status.includes('procesando') || status.includes('generando')) return 'primary';
        if (status.includes('subiendo')) return 'info';
        return 'default';
    };

    // Funciones de descarga
    const extractPathFromUrl = (url) => {
        try {
            const matches = url.match(/\/o\/(.+?)(\?|$)/);
            if (matches && matches[1]) {
                return decodeURIComponent(matches[1]);
            }
            const pathWithoutQueryParams = url.split('?')[0];
            if (
                pathWithoutQueryParams.startsWith('uploads/') ||
                pathWithoutQueryParams.startsWith('reports/')
            ) {
                return pathWithoutQueryParams;
            }
            return url;
        } catch (error) {
            return url;
        }
    };

    const handleViewHtmlReport = async (report) => {
        const fileUrl = report.report_url;
        if (!fileUrl) {
            showAlert(t('reports.layout.fileOrUrlNotAvailable'), 'warning');
            return;
        }
        
        try {
            window.open(fileUrl, '_blank');
        } catch (error) {
            console.error('Error al abrir reporte HTML:', error);
            showAlert(t('generalApiError'), 'error');
        }
    };

    const handleDownloadWord = (report) => {
        const url = report.word_document_url;
        if (!url) {
            showAlert(t('reports.layout.wordNotAvailable'), 'warning');
            return;
        }
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Informe_${report.id.substring(0, 8)}.docx`);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            document.body.removeChild(link);
        }, 100);
    };

    // Ver reporte (navegar para editar)
    const handleViewReport = (reportId) => {
        onEditReport();
        navigate(`/reports/${reportId}`);
    };

    // FUNCIÓN: Reprocesar evaluación
    const handleReprocess = async (reportId) => {
        try {
            setReprocessing(prev => ({ ...prev, [reportId]: true }));

            // Buscar la tarea GPT asociada al reporte
            const { data: tasksData } = await api.get(
                `/gpt-agent/reports/${reportId}/tasks`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            if (!tasksData || !tasksData.data || tasksData.data.length === 0) {
                throw new Error('No se encontró tarea GPT para este reporte');
            }

            // Obtener la primera tarea (la más reciente)
            const taskId = tasksData.data[0].id;

            // Llamar al endpoint de procesar
            await api.post(
                `/gpt-agent/tasks/${taskId}/process`,
                {},
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            showAlert('Reprocesamiento iniciado. La evaluación se regenerará en segundo plano.', 'success');
            
            // Recargar reportes después de un breve delay
            setTimeout(() => {
                loadReports();
            }, 2000);

        } catch (error) {
            console.error('Error al reprocesar:', error);
            showAlert(error.response?.data?.message || t('cvFiltering.reprocessError'), 'error');
        } finally {
            setReprocessing(prev => ({ ...prev, [reportId]: false }));
        }
    };

    // Verificar si el usuario es super-admin
    const isSuperAdmin = user?.rol === 'super-admin';

    // Opciones únicas presentes en los reportes cargados
    const uniqueUsers = [...new Set(reports.map((r) => r.email).filter(Boolean))].sort();
    const uniqueApplicants = [...new Set(reports.map((r) => r.applicant_name).filter(Boolean))].sort();
    const uniquePositions = [...new Set(reports.map((r) => r.position_name).filter(Boolean))].sort();

    // Aplicar filtros client-side: usuario, nombre de postulante y cargo
    const filteredReports = reports.filter((report) => {
        if (userFilter && report.email !== userFilter) return false;
        if (applicantFilter && report.applicant_name !== applicantFilter) return false;
        if (positionFilter && report.position_name !== positionFilter) return false;
        return true;
    });

    return (
        <Box sx={{ width: '100%', py: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" gutterBottom>
                        {t('psycholabor.dashboardTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('psycholabor.dashboardSubTitle')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={loadReports}
                    disabled={loading}
                >
                    {loading ? t('dashboard.loading') : t('dashboard.refresh')}
                </Button>
            </Box>

            {/* Filtros de fecha */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <FilterIcon color="primary" />
                        <Typography variant="h6">
                            {t('dashboard.filters.title')}
                        </Typography>
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
                                <DatePicker
                                    label={t('dashboard.filters.startDate')}
                                    value={startDate ? dayjs(startDate) : null}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            onStartDateChange(newValue.format('YYYY-MM-DD'));
                                        } else {
                                            onStartDateChange('');
                                        }
                                    }}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
                                <DatePicker
                                    label={t('dashboard.filters.endDate')}
                                    value={endDate ? dayjs(endDate) : null}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            onEndDateChange(newValue.format('YYYY-MM-DD'));
                                        } else {
                                            onEndDateChange('');
                                        }
                                    }}
                                    format="YYYY-MM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Button
                                variant="contained"
                                onClick={applyFilters}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : <FilterIcon />}
                                size="large"
                                fullWidth
                            >
                                {t('dashboard.filters.applyFilters')}
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Filtros client-side: usuario, postulante y cargo */}
                    <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={uniqueUsers}
                                value={userFilter}
                                onChange={(event, newValue) => setUserFilter(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('createdBy')}
                                        size="small"
                                    />
                                )}
                                noOptionsText={t('reports.applicant.applicantNoMatch')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={uniqueApplicants}
                                value={applicantFilter}
                                onChange={(event, newValue) => setApplicantFilter(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('reports.reportList.applicantName')}
                                        size="small"
                                    />
                                )}
                                noOptionsText={t('reports.applicant.applicantNoMatch')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={uniquePositions}
                                value={positionFilter}
                                onChange={(event, newValue) => setPositionFilter(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('position')}
                                        size="small"
                                    />
                                )}
                                noOptionsText={t('reports.layout.positionsNotFound')}
                            />
                        </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            {t('dashboard.filters.showingDataFrom', { startDate, endDate })}
                        </Typography>
                    </Alert>
                </CardContent>
            </Card>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Tabla de evaluaciones */}
            {!loading && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('psycholabor.reportId')}</TableCell>
                                <TableCell>{t('reports.reportList.applicantName')}</TableCell>
                                <TableCell>{t('position')}</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>{t('createdBy')}</TableCell>
                                <TableCell>{t('date')}</TableCell>
                                <TableCell align="center">{t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredReports.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            {t('reports.reportList.noReports')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredReports.map((report) => (
                                    <TableRow key={report.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {report.id.substring(0, 8)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{report.applicant_name || t('notAvailable')}</TableCell>
                                        <TableCell>{report.position_name || t('notAvailable')}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={report.status ? (i18n.exists(`status.${report.status}`) ? t(`status.${report.status}`) : report.status) : t('noStatus')}
                                                size="small"
                                                color={getStatusColor(report.status)}
                                            />
                                        </TableCell>
                                        <TableCell>{report.email || t('unknown')}</TableCell>
                                        <TableCell>{formatDate(report.created_at)}</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                {/* Botón Ver HTML */}
                                                {report.report_url && (
                                                    <Tooltip title={t('reports.layout.showHtmlReport')}>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleViewHtmlReport(report)}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {/* Botón Descargar Word */}
                                                {report.word_document_url && (
                                                    <Tooltip title={t('downloadWord')}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleDownloadWord(report)}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {/* Botón Reprocesar (solo super-admin) */}
                                                {isSuperAdmin && (
                                                    <Tooltip title={t('cvFiltering.reprocess')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => handleReprocess(report.id)}
                                                                disabled={reprocessing[report.id]}
                                                            >
                                                                {reprocessing[report.id] ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <AutorenewIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {/* Botón Editar (navegar al reporte) */}
                                                <Tooltip title={t('edit')}>
                                                    <IconButton
                                                        size="small"
                                                        color="default"
                                                        onClick={() => handleViewReport(report.id)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Contador de resultados */}
            {!loading && filteredReports.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('psycholabor.totalReports', { count: filteredReports.length })}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default PsycholaborDashboard;